import { Colors, Fonts } from "@/theme";
import React from "react";
import styled from "styled-components";
import { Paragraph } from "../typography";
import { BORDER_RADIUS, BOX_SHADOW, desktop, mobile } from "@/theme/common";
import { ButtonGameRegular } from "../buttons/button-game-regular";
import { AutomaticActionButton } from "../buttons/automatic-action-button";

export enum NotificationType {
  SUCCESS = "success",
  ERROR = "error",
  DEFAULT = "default",
}
interface NotificationProps {
  type: NotificationType;
  avatar: string;
  title: string;
  name?: string;
  subtitle?: string;
  buttonText: string;
  onButtonClick: () => void;
  automaticAction?: boolean;
  automaticActionDurationMs?: number;
}

const NotificationWrapper = styled.div<{ type: string }>`
  position: fixed;
  z-index: 10;
  bottom: 0px;
  left: 0px;
  right: 0px;
  background-color: ${({ type }) =>
    type === "success"
      ? Colors.green200
      : type === "error"
      ? Colors.red200
      : Colors.gray100};
  border-radius: ${BORDER_RADIUS.XXL};
  padding: 16px 16px 24px 16px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Avatar = styled.img`
  width: 45px;
  height: 45px;
  border-radius: ${BORDER_RADIUS.XL};
  flex-shrink: 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: fit-content;
  max-width: 480px;
  width: 100%;
`;

const RowContainer = styled.div<{ $marginTop?: number }>`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: ${({ $marginTop }) => $marginTop ?? 0}px;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.div<{ type: NotificationType }>`
  position: relative;
  font-size: 16px;
  font-family: ${Fonts.ttNormsProBold};
  background-color: ${Colors.white};
  padding: 6px 10px;
  border-radius: ${BORDER_RADIUS.MD};
  box-shadow: ${BOX_SHADOW.XS};
  width: fit-content;
  color: ${({ type }) => getTitleColor(type)};

  &::before {
    content: "";
    position: absolute;
    left: -9px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 6px 9px 6px 0;
    border-color: transparent ${Colors.white} transparent transparent;
  }
`;

const ChefName = styled.div<{ type: NotificationType }>`
  font-size: 13px;
  font-family: ${Fonts.ttNormsProNormal};
  color: ${({ type }) => getSubtitleColor(type)};
`;

const Subtitle = styled(Paragraph)<{ type: NotificationType }>`
  font-size: 14px;
  line-height: 1.3;
  color: ${({ type }) => getSubtitleColor(type)};
  margin-top: 4px;
`;

export const ContinueButton = styled(ButtonGameRegular)`
  min-width: 291px;
  max-width: unset;
  ${desktop(`
    width: 480px;
  `)}

  ${mobile(`
    width: 100%;
  `)}
`;

/**
 * Helpers
 */

const getButtonColor = (type: NotificationType) => {
  switch (type) {
    case NotificationType.SUCCESS:
      return "green500";
    case NotificationType.ERROR:
      return "red500";
    case NotificationType.DEFAULT:
      return "black";
    default:
      return "black";
  }
};

const getTitleColor = (type: NotificationType) => {
  switch (type) {
    case NotificationType.SUCCESS:
      return Colors.green700;
    case NotificationType.ERROR:
      return Colors.red700;
    case NotificationType.DEFAULT:
      return Colors.gray400;
    default:
      return Colors.gray400;
  }
};

const getBorderColor = (type: NotificationType) => {
  switch (type) {
    case NotificationType.SUCCESS:
      return "green700";
    case NotificationType.ERROR:
      return "red700";
    case NotificationType.DEFAULT:
      return "black";
    default:
      return "gray200";
  }
};

const getSubtitleColor = (type: NotificationType) => {
  switch (type) {
    case NotificationType.SUCCESS:
      return Colors.green700;
    case NotificationType.ERROR:
      return Colors.red700;
    case NotificationType.DEFAULT:
      return Colors.gray400;
    default:
      return Colors.gray400;
  }
};

/**
 * NotificationModal Component
 */

export const NotificationModal = ({
  type,
  avatar,
  title,
  name,
  subtitle,
  buttonText,
  onButtonClick,
  automaticAction = false,
  automaticActionDurationMs = 15000, // 15s default
}: NotificationProps) => {
  return (
    <NotificationWrapper type={type}>
      <ContentWrapper>
        <RowContainer>
          {avatar && <Avatar src={avatar} alt="Avatar" />}

          <Title type={type}>
            <ColumnContainer>
              {name && <ChefName type={type}>{name}</ChefName>}
              {title}
            </ColumnContainer>
          </Title>
        </RowContainer>
        {subtitle && <Subtitle type={type}>{subtitle}</Subtitle>}

        <RowContainer $marginTop={16}>
          {automaticAction ? (
            <AutomaticActionButton
              text={buttonText}
              color="white"
              backgroundColor={getButtonColor(type)}
              progressColor={getBorderColor(type)}
              borderColor={getBorderColor(type)}
              onClick={onButtonClick}
              durationMs={automaticActionDurationMs}
            />
          ) : (
            <ContinueButton
              text={buttonText}
              color={getButtonColor(type)}
              borderColor={getBorderColor(type)}
              onClick={onButtonClick}
            />
          )}
        </RowContainer>
      </ContentWrapper>
    </NotificationWrapper>
  );
};
